import { getCommitTag } from '@doxyme/lib-version';
import { type LogConfig, isLogLevelType } from '@doxyme/analytics';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import { getFrontendConfigLoader } from '@doxyme/config';
import {
  type GetSessionId,
  createDatadogRumBeforeSend,
} from './beforeSendRum.datadog';

type DataDogCommonOptions = {
  service: string;
  env: string;
  version: string;
  site: string;
  clientToken: string;
};

/**
 * Common properties for DataDog Logs and RUM.
 * @returns DataDogCommonOptions
 */
export const getDataDogCommonOptions = (): DataDogCommonOptions => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const commitTag: string | null = getCommitTag();

  return {
    service:
      (getFrontendConfigLoader().get('SERVICE_NAME') as string) || 'apollo',
    env:
      (getFrontendConfigLoader().get('DATADOG_ENVIRONMENT') as string) ||
      (getFrontendConfigLoader().get('ENV') as string) ||
      'development',
    version: commitTag ?? 'unknown',
    site: 'datadoghq.com',
    clientToken: getFrontendConfigLoader().get(
      'DATADOG_CLIENT_TOKEN',
    ) as string,
  };
};

/**
 * Constructs options for use with constructing a LogConfig
 * @returns DataDog-specific options for LogConfig
 */
/* istanbul ignore next */
export const getDataDogLoggerOptions = (): LogConfig => {
  const datadogLogLevel = getFrontendConfigLoader().getOptional(
    'DATADOG_LOG_LEVEL',
  ) as string;
  const {
    site: datadogSite,
    clientToken: datadogClientToken,
    ...otherCommonOptions
  } = getDataDogCommonOptions();

  return {
    ...otherCommonOptions,
    datadogSite,
    datadogClientToken,
    logLevel: isLogLevelType(datadogLogLevel) ? datadogLogLevel : 'trace',
    isDefaultDisabled:
      (getFrontendConfigLoader().get('ENV') as string) === 'production',
    datadogEnabled: getFrontendConfigLoader().get(
      'DATADOG_LOGGING_ENABLED',
    ) as boolean,
  };
};

/**
 * Gets whether or not Datadog RUM is enabled
 * @returns {boolean} Whether or not Datadog RUM is enabled
 */
/* istanbul ignore next */
export const getIsDataDogRumEnabled = (): boolean =>
  getFrontendConfigLoader().get('DATADOG_RUM_ENABLED') as boolean;

/**
 * getDataDogRumInitOptions
 * @description Constructs options to be passed to Datadog Rum when initializing
 * (`datadogRum.init(...)`)
 * @returns {RumInitConfiguration} RumInitConfiguration for initializing datadogRum
 */
/* istanbul ignore next */
export const getDataDogRumInitOptions = (
  getSessionId: GetSessionId,
): RumInitConfiguration => {
  const config: RumInitConfiguration = {
    ...getDataDogCommonOptions(),
    applicationId: getFrontendConfigLoader().get(
      'DATADOG_RUM_APPLICATION_ID',
    ) as string,
    trackInteractions: true,
    trackSessionAcrossSubdomains: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingOrigins: [
      window.location.origin,
      getFrontendConfigLoader().get('FRONTEND_URL') as string,
    ],
    beforeSend: createDatadogRumBeforeSend(getSessionId),

    /**
     * Please do not increase `sessionSampleRate` or `sessionReplaySampleRate`
     * without first consulting Ben Anderson-Dukes and Myles Sanigar.
     *
     * Note: these numbers are percentages: 0-100
     *
     * Note: the actual session-replay sample rate is calculated as
     * `sessionSampleRate`% * `sessionReplaySampleRate`%
     */
    sessionSampleRate: Number(
      getFrontendConfigLoader().getOptional('DATADOG_SAMPLE_RATE') ?? '0',
    ),
    sessionReplaySampleRate: Number(
      getFrontendConfigLoader().getOptional('DATADOG_REPLAY_SAMPLE_RATE') ??
        '0',
    ),
  };

  return config;
};
